import { Routes, Route, BrowserRouter } from 'react-router-dom'
import { useAuthStore } from './store/useAuthStore'
import { useEffect, useState } from 'react'
import { SEOHead, ErrorBoundary, PerformanceOptimizer, SkipLink } from './components/SEO'
import { HelmetProvider } from 'react-helmet-async'
import { checkDatabaseSetup } from './utils/simpleSetup'

// Public Pages
import LandingPage from './pages/LandingPage'
import ServicesPage from './pages/ServicesPage'
import ProductsPage from './pages/ProductsPage'
import ProductDetailPage from './pages/ProductDetailPage'
import ContactPage from './pages/ContactPage'
import AppointmentPage from './pages/AppointmentPage'
import CartPage from './pages/CartPage'
import CheckoutPage from './pages/CheckoutPage'
import OrderPage from './pages/OrderPage'

// Auth Pages
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import ResetPasswordPage from './pages/ResetPasswordPage'

// Dashboard Pages
import AdminDashboard from './pages/AdminDashboard'
import EmployeeDashboard from './pages/EmployeeDashboard'
import ClientDashboard from './pages/ClientDashboard'
import ProjectPage from './pages/ProjectPage'
import ProjectManagement from './pages/ProjectManagement'
import ProjectManagementPage from './pages/ProjectManagementPage'
import TimeTrackingPage from './pages/TimeTrackingPage'
import AnalyticsDashboard from './pages/AnalyticsDashboard'
import InvoiceManagementPage from './pages/InvoiceManagementPage'
import DashboardRedirect from './pages/DashboardRedirect'

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute'

function App() {
  const { initializeAuth, loading: authLoading } = useAuthStore()
  const [initializing, setInitializing] = useState(true)

  useEffect(() => {
    let mounted = true
    const init = async () => {
      try {
        // Check database setup on app start (non-blocking)
        checkDatabaseSetup().catch(() => {})
        // Initialize auth
        await initializeAuth()
      } catch (err) {
        console.error('App initialization error:', err)
      } finally {
        // Always show content after a short delay, even if auth is still loading
        setTimeout(() => {
          if (mounted) setInitializing(false)
        }, 500)
      }
    }
    init()
    return () => { mounted = false }
  }, [initializeAuth])

  // Show loading screen only during initial setup, not during auth state changes
  if (initializing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">One Matrix</h2>
          <p className="text-sm text-gray-600">Loading your experience...</p>
        </div>
      </div>
    )
  }

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="min-h-screen bg-gray-50">
            <SEOHead />
            <SkipLink />
            <PerformanceOptimizer />
            <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/:id" element={<ProductDetailPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/appointment" element={<AppointmentPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/dashboard" element={<DashboardRedirect />} />

        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset" element={<ResetPasswordPage />} />

        {/* Protected Dashboard Routes */}
        <Route 
          path="/admin/*" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employee/*" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'employee']}>
              <EmployeeDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/employee/time-tracking" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'employee']}>
              <TimeTrackingPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/client/*" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'employee', 'client']}>
              <ClientDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/project/:id" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'employee', 'client']}>
              <ProjectPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/project/:id" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <ProjectManagementPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/analytics" 
          element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AnalyticsDashboard />
            </ProtectedRoute>
          } 
        />
            <Route
              path="/admin/invoices"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <InvoiceManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/projects"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ProjectManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/projects"
              element={
                <ProtectedRoute allowedRoles={['employee']}>
                  <ProjectManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/projects"
              element={
                <ProtectedRoute allowedRoles={['client']}>
                  <ProjectManagement />
                </ProtectedRoute>
              }
            />
        </Routes>
          </div>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  )
}

export default App
